import { type ReactNode, useContext } from 'react';
import { InCardContext } from '~/components/ui/card/Card';
import { Skeleton } from '~/components/ui/skeleton';
import {
  TABLE_BODY,
  TABLE_DIVIDER,
  TABLE_FRAME,
  TABLE_HEAD_ROW,
  TABLE_PINNED_ACTIONS,
  TABLE_ROW,
  TABLE_TD,
  tablePinnedStart,
  TABLE_TH,
  hasDivider,
} from '~/components/ui/table/tokens';

/** Varied so the placeholder rows read as text of different lengths, not a grid of equal bars. */
const CELL_WIDTHS = ['w-2/3', 'w-1/2', 'w-3/4', 'w-2/5', 'w-3/5'];

export interface TableSkeletonColumn {
  /** `'actions'` gets the short centred bar (and the pinned cell when `pinActions` is set). */
  id?: string;
  /** The real header. Omit it for a placeholder bar when the columns are not known yet. */
  header?: ReactNode;
  /** Width class for the column, e.g. `w-28`. */
  className?: string;
}

interface TableRowsSkeletonProps {
  columns: TableSkeletonColumn[];
  rows?: number;
  /** Pin the first column (and a leading `select` column) left and the `actions` column right, as `DataTable` does. */
  pinActions?: boolean;
  /** Defaults to framed outside a `Card` and unframed inside one; a card's table never has an outer border. */
  framed?: boolean;
  testId?: string;
}

/** A table's first load: the header over placeholder rows (DESIGN.md §11.1). The only table skeleton; `DataTable` and `PanelTable` render it for `loading`. */
export function TableRowsSkeleton({
  columns,
  rows = 8,
  pinActions,
  framed: framedProp,
  testId,
}: TableRowsSkeletonProps) {
  const inCard = useContext(InCardContext);
  const framed = framedProp ?? !inCard;
  const ids = columns.map((column) => column.id);
  // Mirrors DataTable: a leading checkbox column pins at left-0 and the first data column beside it at left-16.
  const pinStart = (index: number) => {
    if (!pinActions || ids[index] === 'actions') return '';
    if (index === 0) return tablePinnedStart('left-0');
    return index === 1 && ids[0] === 'select' ? tablePinnedStart('left-16') : '';
  };
  return (
    <div className="w-full" role="status" aria-busy="true" data-testid={testId}>
      <span className="sr-only">Loading…</span>
      <div className={framed ? TABLE_FRAME : undefined}>
        <div className="overflow-x-auto">
          <table className="min-w-full" aria-hidden="true">
            <thead>
              <tr className={TABLE_HEAD_ROW}>
                {columns.map((column, index) => {
                  const actions = column.id === 'actions';
                  return (
                    <th
                      key={index}
                      className={`${TABLE_TH} ${actions ? `w-28 text-center${pinActions ? ` ${TABLE_PINNED_ACTIONS}` : ''}` : 'text-left'} ${pinStart(index) || (hasDivider(ids, index) ? TABLE_DIVIDER : '')} ${column.className ?? ''}`}
                    >
                      {column.header === undefined ? (
                        <Skeleton className="h-3 w-20" />
                      ) : (
                        column.header
                      )}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody className={TABLE_BODY}>
              {Array.from({ length: rows }, (_, row) => (
                <tr key={row} className={TABLE_ROW}>
                  {columns.map((column, index) => {
                    const actions = column.id === 'actions';
                    return (
                      <td
                        key={index}
                        className={`${TABLE_TD} ${pinStart(index) || (hasDivider(ids, index) ? TABLE_DIVIDER : '')} ${actions && pinActions ? TABLE_PINNED_ACTIONS : ''}`}
                      >
                        {actions ? (
                          <Skeleton className="mx-auto h-4 w-6" />
                        ) : (
                          <Skeleton
                            className={`h-4 ${CELL_WIDTHS[(row + index) % CELL_WIDTHS.length]}`}
                          />
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
